import _ from 'lodash';
import { parseISO, getDate, startOfMonth, endOfMonth, format } from 'date-fns';

import { ISO_DATE_FORMAT } from '@constants/dates';

import { TransactionsFilter } from '../types';
import { CASH_FLOW_CATEGORIES } from '../constants';
import { createWindowKey } from '../utils';

export const updateSingleProfile = (state, name, data) => {
  const newState = { ...state };
  newState[name] = data;
  return newState;
};

const createTransactionData = () => ({
  transactions: [],
  monthRollups: {},
  lastPageReached: false,
  lastRequestCompleted: null,
  needsRefresh: false,
});

const getTransactionData = (state, filter) => {
  if (!filter || _.keys(filter).length === 0) {
    return state.buckets.all;
  }
  const key = _.keys(filter)[0];
  const value = filter[key];
  if (!state.buckets[key]) {
    return undefined;
  }
  return state.buckets[key][value];
};

export const getOrCreateTransactionData = (state, filter = {}) =>
  getTransactionData(state, filter) || createTransactionData();

/**
 * Updates the redux state given transactionData and the desired filter.
 * Returns the new redux state.
 * */
export const updateStateWithTransactionData = (
  // The current transactions redux state
  state: Record<string, any>,
  // A transactionData object
  data: Record<string, any>,
  // A filter for transaction data
  filter: Record<string, any>,
): Record<string, any> => {
  if (!filter || _.keys(filter).length === 0) {
    return {
      ...state,
      buckets: {
        ...state.buckets,
        all: {
          ...data,
        },
      },
    };
  }

  const key = _.keys(filter)[0];
  const value = filter[key];
  return {
    ...state,
    buckets: {
      ...state.buckets,
      [key]: {
        ...state.buckets[key],
        [value]: {
          ...data,
        },
      },
    },
  };
};

/**
 * Retrieves all transactions from the buckets, note that this is not representative of
 * all the transactions a user might have since transactions can exist that are not
 * in any bucket. For this reason, to access all transactions it is recommended to use
 * the `getAllTransactions` Yodlee selector.
 *
 * @deprecated Use {@link getAllTransactions}
 * */
export const getAllTransactionsFromBuckets = (buckets) =>
  _.reduce(
    buckets,
    (transactions, bucket, bucketKey) => {
      if (bucketKey === 'all') {
        return _.unionBy(transactions, bucket?.transactions, 'id');
      }

      const filterTransactions = _.reduce(
        bucket,
        (t, v) => _.unionBy(t, v?.transactions, 'id'),
        [],
      );

      return _.unionBy(transactions, filterTransactions, 'id');
    },
    [],
  );

/**
 * Returns an object representing the spending for each day in a month.
 *
 * @return {Object} Shape is {[dayOfMonth]: float, ...}
 */
export const getSpendingByDayOfMonth = (
  // Shape is {"2018-01-01": 40.50, ...}
  spendingByDay: Record<string, number>,
): Record<string, number> =>
  _.reduce(
    spendingByDay,
    (acc, amount, dateStr) => {
      const date = parseISO(dateStr);
      return {
        ...acc,
        [getDate(date)]: amount,
      };
    },
    {},
  );

export const createMonthWindowKey = (dayInTargetMonth: Date) => {
  const startOfTargetMonth = startOfMonth(dayInTargetMonth);
  const endOfTargetMonth = endOfMonth(dayInTargetMonth);

  return createWindowKey(startOfTargetMonth, endOfTargetMonth);
};

export const getTopSpendingSlice = (
  sliceData,
  sliceName: string,
  startDate: Date,
  endDate: Date,
) => {
  const startDateStr = format(startDate, ISO_DATE_FORMAT);
  const endDateStr = format(endDate, ISO_DATE_FORMAT);

  switch (sliceName) {
    case 'monthly':
      return _.reduce(
        sliceData,
        (acc, { top_sums: topSums, month }) => ({
          ...acc,
          [month]: topSums,
        }),
        {},
      );
    case 'weekly':
      return _.reduce(
        sliceData,
        (acc, { top_sums: topSums, week }) => ({ ...acc, [week]: topSums }),
        {},
      );
    case 'window':
      return { [`${startDateStr}-${endDateStr}`]: sliceData };
    default:
      return null;
  }
};

/**
 * Updates the spending/earning cash flow rollups for a given month.
 *
 * @return {Object} State with updated cash flow rollups
 */
export const updateTransactionsMonthCashFlow = (
  state: Record<string, any>,
  filter: TransactionsFilter,
  dayInTargetMonth: Date,
  spending: number,
  earning: number,
): Record<string, any> => {
  const bucket = getOrCreateTransactionData(state, filter);
  const firstDayInTargetMonthStr = format(
    startOfMonth(dayInTargetMonth),
    ISO_DATE_FORMAT,
  );

  if (_.has(filter, 'category')) {
    // We do not use this endpoint for category rollups, so don't make any changes here
    return state;
  }

  if (_.isEqual(filter, {})) {
    // Continue to store spending/earning in the "all" bucket for legacy purposes
    const newAllBucket = {
      ...bucket,
      monthRollups: {
        ...bucket.monthRollups,
        [firstDayInTargetMonthStr]: {
          ...bucket.monthRollups[firstDayInTargetMonthStr],
          sum: spending,
        },
      },
    };

    let newState = state;
    newState = updateStateWithTransactionData(newState, newAllBucket, {});

    // add spending and earning to the cashFlowRollups slice
    newState = {
      ...newState,
      cashFlowRollups: {
        ...newState.cashFlowRollups,
        monthly: {
          ...newState?.cashFlowRollups.monthly,
          [firstDayInTargetMonthStr]: {
            [CASH_FLOW_CATEGORIES.SPENDING]: spending,
            [CASH_FLOW_CATEGORIES.EARNING]: earning,
          },
        },
      },
    };

    return newState;
  }

  // Handle non-category filters (e.g. merchantName, accountId)
  const newBucket = {
    ...bucket,
    monthRollups: {
      ...bucket.monthRollups,
      [firstDayInTargetMonthStr]: {
        ...bucket.monthRollups[firstDayInTargetMonthStr],
        sum: spending,
      },
    },
  };

  return updateStateWithTransactionData(state, newBucket, filter);
};
